/**
 * Passenger or driver self-registration — gated by organisation invite code.
 * URL: /register/:slug  optional query: ?role=driver (default passenger)
 */

import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ForgotPasswordLink from '../components/ForgotPasswordLink';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../authContext';
import { uploadDriverLicense } from '../lib/driverLicense';
import { AGE_RANGE_OPTIONS } from '../lib/passengers';
import { DRIVER_LICENSE_OPTIONS } from '../lib/transport';
import { signUpWithOrgProfile } from '../lib/auth';

const accent = '#0d7377';
const bg = '#0f1419';
const card = '#1a2332';
const text = '#e8eaed';
const muted = '#9aa0a6';

export default function Register() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const roleParam = searchParams.get('role');
  const registrationRole = roleParam === 'driver' ? 'driver' : 'passenger';

  const [inviteCode, setInviteCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [licenseType, setLicenseType] = useState('auto');
  const [licenseFile, setLicenseFile] = useState(null);
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupArea, setPickupArea] = useState('');
  const [phone, setPhone] = useState('');
  const [ageRange, setAgeRange] = useState('adult');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const title = useMemo(
    () => (registrationRole === 'driver' ? 'Driver registration' : 'Passenger registration'),
    [registrationRole],
  );

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (registrationRole === 'driver' && !licenseFile) {
        setError('Upload your driving licence to register as a driver.');
        return;
      }

      const { user, error: signErr } = await signUpWithOrgProfile(
        email,
        password,
        fullName,
        slug,
        inviteCode,
        registrationRole,
        {
          licenseType: registrationRole === 'driver' ? licenseType : undefined,
          pickupAddress: registrationRole === 'passenger' ? pickupAddress : undefined,
          pickupArea: registrationRole === 'passenger' ? pickupArea : undefined,
          ageRange: registrationRole === 'passenger' ? ageRange : undefined,
          phone: registrationRole === 'passenger' ? phone : undefined,
        },
      );

      if (signErr) {
        setError(signErr.message || 'Registration failed.');
        return;
      }

      if (registrationRole === 'driver' && licenseFile && user?.id) {
        const { error: uploadErr } = await uploadDriverLicense(user.id, licenseFile);
        if (uploadErr) {
          setError(`Account created but licence upload failed: ${uploadErr.message}. You can upload it from the pending page.`);
          await refresh();
          navigate('/driver/pending', { replace: true });
          return;
        }
      }

      await refresh();
      if (registrationRole === 'driver') {
        navigate('/driver/pending', { replace: true });
      } else {
        navigate('/passenger', { replace: true });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: bg,
        color: text,
        fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        padding: '24px 16px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: 440, margin: '0 auto' }}>
        <header style={{ marginBottom: 24, textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, margin: '0 0 8px' }}>{title}</h1>
          <p style={{ margin: 0, color: muted, fontSize: '0.95rem' }}>
            Organisation slug: <strong style={{ color: text }}>{slug}</strong>
            {' · '}
            Role: <strong style={{ color: text }}>{registrationRole}</strong>
          </p>
        </header>

        <div
          style={{
            background: card,
            borderRadius: 14,
            padding: 22,
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
          }}
        >
          <p style={{ marginTop: 0, fontSize: '0.88rem', color: muted, lineHeight: 1.5 }}>
            Enter the invite code your organisation shared with you. Admins can add drivers by sharing this same page
            with <code style={{ color: accent }}>?role=driver</code> in the URL.
          </p>

          <form onSubmit={handleSubmit}>
            <label style={labelStyle}>Invite code</label>
            <input
              required
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              style={inputStyle}
              autoComplete="off"
              placeholder="e.g. SCHOOL-2025"
            />

            <label style={labelStyle}>Full name</label>
            <input
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={inputStyle}
              autoComplete="name"
            />

            <label style={labelStyle}>Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              autoComplete="email"
            />

            <label style={labelStyle}>Password</label>
            <PasswordInput
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              inputStyle={inputStyle}
              autoComplete="new-password"
            />
            <ForgotPasswordLink scope="team" />

            {registrationRole === 'driver' ? (
              <>
                <label style={labelStyle}>Licence type</label>
                <select required value={licenseType} onChange={(e) => setLicenseType(e.target.value)} style={inputStyle}>
                  {DRIVER_LICENSE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <label style={labelStyle}>Upload driving licence (JPG, PNG, or PDF)</label>
                <input
                  type="file"
                  required
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={(e) => setLicenseFile(e.target.files?.[0] || null)}
                  style={{ ...inputStyle, padding: '8px 10px' }}
                />
                <p style={{ margin: '6px 0 0', fontSize: '0.82rem', color: muted }}>
                  Your admin will review this when approving your application.
                </p>
              </>
            ) : (
              <>
                <label style={labelStyle}>Your age range</label>
                <select required value={ageRange} onChange={(e) => setAgeRange(e.target.value)} style={inputStyle}>
                  {AGE_RANGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <label style={labelStyle}>Pickup area</label>
                <input
                  required
                  value={pickupArea}
                  onChange={(e) => setPickupArea(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. Gateshead, Lekki, Victoria Island"
                  autoComplete="address-level2"
                />
                <label style={labelStyle}>Pickup address (optional)</label>
                <input
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  style={inputStyle}
                  placeholder="Street or landmark"
                  autoComplete="street-address"
                />
                <label style={labelStyle}>Phone (optional)</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  style={inputStyle}
                  placeholder="e.g. +44 7700 900123"
                  autoComplete="tel"
                />
              </>
            )}

            {error ? (
              <p style={{ color: '#f28b82', fontSize: '0.9rem', marginTop: 12, marginBottom: 0 }}>{error}</p>
            ) : null}

            <button type="submit" disabled={busy} style={submitStyle(busy)}>
              {busy ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <p style={{ marginTop: 18, marginBottom: 0, textAlign: 'center', fontSize: '0.9rem' }}>
            <Link to="/login" style={{ color: accent, textDecoration: 'none', fontWeight: 500 }}>
              Already have an account? Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', marginBottom: 6, marginTop: 14, fontSize: '0.85rem', color: muted };
const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.12)',
  background: '#0f1419',
  color: text,
  fontSize: '1rem',
  outline: 'none',
};

function submitStyle(busy) {
  return {
    marginTop: 22,
    width: '100%',
    padding: '14px 16px',
    borderRadius: 10,
    border: 'none',
    background: `linear-gradient(180deg, ${accent}, #0a5c5f)`,
    color: '#fff',
    fontWeight: 600,
    fontSize: '1rem',
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.85 : 1,
  };
}

